import React, { useContext, useEffect, useMemo, useState } from 'react';
import { css } from '@emotion/react';
import {
    CircularProgress,
    Grid,
    LinearProgress,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    MenuItem,
    TextField,
} from '@material-ui/core';
import { CheckCircle } from '@material-ui/icons';
import * as Sentry from '@sentry/react';
import { useLazyQuery, useMutation } from '@apollo/client';

import { UserContext } from '@/components/App';
import NavResponsiveModal, {
    ModalActions,
    ModalContent,
    ModalHeader,
} from '@/components/Navigation/NavResponsiveModal';
import { DebouncedTextField, PrimaryButton, SecondaryButton } from '@/styles/blocks';
import { greens, greys } from '@/styles/colors';
import {
    GET_CORP_INTEGRATION_OVERRIDES,
    INSERT_CORP_INTEGRATION_OVERRIDE,
    SEARCH_CONNECTABLE_CARRIERS,
} from '../graphql';
import { CorpAdminContext } from '../store';

const SEARCH_MIN_CHARS = 2;

const ConnectCarrierModal = ({ open, onClose }) => {
    const { admin_clients } = useContext(UserContext);
    const { state, callbacks } = useContext(CorpAdminContext);
    const { assetHeavy, assetLight } = state;
    const { setNotification } = callbacks;

    const [shipperId, setShipperId] = useState('');
    const [carrierId, setCarrierId] = useState(null);
    const [search, setSearch] = useState('');

    const orgShippers = useMemo(
        () => (admin_clients || []).filter((c) => !!c.user?.roles?.['SHIPPER']),
        [admin_clients]
    );
    const orgShipperIds = useMemo(() => orgShippers.map((s) => s.client_id), [orgShippers]);

    // Already-connected carriers — anything that already has a row under this shipper in
    // either asset map (overrides plus pricing relationships).
    const connectedCarrierIds = useMemo(() => {
        if (!shipperId) return new Set();
        const ids = new Set();
        const accumulate = (map) =>
            Object.entries(map || {}).forEach(([carrier_id, data]) => {
                if (data?.[shipperId]) ids.add(carrier_id);
            });
        accumulate(assetHeavy);
        accumulate(assetLight);
        return ids;
    }, [assetHeavy, assetLight, shipperId]);

    const [runSearch, { data: searchData, loading: searchLoading }] = useLazyQuery(SEARCH_CONNECTABLE_CARRIERS, {
        fetchPolicy: 'network-only',
    });

    useEffect(() => {
        const trimmed = search.trim();
        if (!open || trimmed.length < SEARCH_MIN_CHARS) return;
        runSearch({ variables: { search: `%${trimmed}%` } });
    }, [open, search, runSearch]);

    const carrierResults = useMemo(() => {
        const all = searchData?.clients || [];
        return all.filter((c) => !connectedCarrierIds.has(c.client_id));
    }, [searchData, connectedCarrierIds]);

    const [insertOverride, { loading: insertLoading }] = useMutation(INSERT_CORP_INTEGRATION_OVERRIDE, {
        update(cache, { data }) {
            const newOverride = data?.insert_integration_overrides_one;
            if (!newOverride) return;
            try {
                const existing = cache.readQuery({
                    query: GET_CORP_INTEGRATION_OVERRIDES,
                    variables: { orgShipperIds },
                });
                if (!existing) return;
                cache.writeQuery({
                    query: GET_CORP_INTEGRATION_OVERRIDES,
                    variables: { orgShipperIds },
                    data: {
                        integration_overrides: [...existing.integration_overrides, newOverride],
                    },
                });
            } catch (err) {
                // Cache miss is non-fatal — store will pick it up on next read.
            }
        },
    });

    const handleClose = () => {
        setShipperId('');
        setCarrierId(null);
        setSearch('');
        onClose();
    };

    const handleSubmit = async () => {
        if (!shipperId || !carrierId) return;
        try {
            await insertOverride({
                variables: {
                    object: { carrier_id: carrierId, shipper_id: shipperId, client_id: carrierId, type: 'MANUAL' },
                },
            });
            setNotification?.({ severity: 'success', message: 'Carrier connected' });
            handleClose();
        } catch (err) {
            Sentry.captureException(err);
            setNotification?.({ severity: 'error', message: 'Error connecting carrier' });
        }
    };

    const showResults = !!shipperId && search.trim().length >= SEARCH_MIN_CHARS;

    return (
        <NavResponsiveModal open={open} onClose={handleClose} maxWidth="sm" fullWidth>
            <ModalHeader
                title="Connect Carrier"
                onClose={handleClose}
                css={css`
                    border-bottom: 1px solid lightgray;
                `}
            />
            {insertLoading && (
                <LinearProgress
                    color="primary"
                    css={css`
                        width: 100%;
                    `}
                />
            )}
            <ModalContent>
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <TextField
                            select
                            variant="outlined"
                            fullWidth
                            label="Shipper"
                            value={shipperId}
                            onChange={(e) => {
                                setShipperId(e.target.value);
                                setCarrierId(null);
                            }}
                        >
                            {orgShippers.length === 0 ? (
                                <MenuItem disabled value="">
                                    No shippers available
                                </MenuItem>
                            ) : (
                                orgShippers.map((s) => (
                                    <MenuItem key={s.client_id} value={s.client_id}>
                                        {s.business_name || s.email || s.client_id}
                                    </MenuItem>
                                ))
                            )}
                        </TextField>
                    </Grid>
                    <Grid item xs={12}>
                        <DebouncedTextField
                            label="Search carriers by name or email"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            disabled={!shipperId}
                            helperText={!shipperId ? 'Select a shipper first' : null}
                        />
                    </Grid>
                    {showResults && (
                        <Grid item xs={12}>
                            {searchLoading ? (
                                <div
                                    css={css`
                                        display: flex;
                                        justify-content: center;
                                        padding: 1rem;
                                    `}
                                >
                                    <CircularProgress size={24} />
                                </div>
                            ) : carrierResults.length === 0 ? (
                                <span
                                    css={css`
                                        color: ${greys[2]};
                                    `}
                                >
                                    No matching carriers
                                </span>
                            ) : (
                                <List
                                    dense
                                    css={css`
                                        max-height: 300px;
                                        overflow-y: auto;
                                        border: 1px solid ${greys[5]};
                                        border-radius: 4px;
                                    `}
                                >
                                    {carrierResults.map((c) => {
                                        const isSelected = carrierId === c.client_id;
                                        return (
                                            <ListItem
                                                key={c.client_id}
                                                button
                                                selected={isSelected}
                                                onClick={() => setCarrierId(c.client_id)}
                                                css={css`
                                                    border-left: 3px solid
                                                        ${isSelected ? greens.primary : 'transparent'};
                                                    transition: background-color 120ms ease, border-color 120ms ease;
                                                    &.Mui-selected,
                                                    &.Mui-selected:hover {
                                                        background-color: ${greens.primary}1f;
                                                    }
                                                `}
                                            >
                                                <ListItemText
                                                    primary={c.business_name || '(no name)'}
                                                    secondary={c.email || c.client_id}
                                                    primaryTypographyProps={{
                                                        style: isSelected
                                                            ? { color: greens.primary, fontWeight: 600 }
                                                            : undefined,
                                                    }}
                                                />
                                                {isSelected && (
                                                    <ListItemIcon
                                                        css={css`
                                                            min-width: 0;
                                                            color: ${greens.primary};
                                                        `}
                                                    >
                                                        <CheckCircle />
                                                    </ListItemIcon>
                                                )}
                                            </ListItem>
                                        );
                                    })}
                                </List>
                            )}
                        </Grid>
                    )}
                </Grid>
            </ModalContent>
            <ModalActions>
                <SecondaryButton onClick={handleClose}>Cancel</SecondaryButton>
                <PrimaryButton disabled={!shipperId || !carrierId || insertLoading} onClick={handleSubmit}>
                    Connect
                </PrimaryButton>
            </ModalActions>
        </NavResponsiveModal>
    );
};

export default ConnectCarrierModal;
